import { watch, type App, type Plugin } from 'vue';
import type { Router } from 'vue-router';
import axios from 'axios';

import { useAppSidebarMenuStore, type SidebarItemType } from '../stores/app-sidebar-menu';
import { useAppContextStore } from './layouts/context';

import type { AuthUser } from '../types';

type AppContextStore = ReturnType<typeof useAppContextStore>;

export type SidebarOptions = {
	/** Builds the menu; called again whenever the user or the active context changes. */
	menu: (user: AuthUser, context: AppContextStore) => Array<SidebarItemType>;
	/**
	 * Endpoint below the context path answering `{ "<label>": <count> }`, e.g. `me/sidebar_badges`.
	 * Read after every menu rebuild and every route change. No endpoint, no badges.
	 */
	badges?: string;
};

/** Writes the counts onto the entries that carry the matching label; a count of zero clears the badge. */
function applyBadges(items: Array<SidebarItemType>, counts: Record<string, number>): void {
	for (const item of items) {
		if ('children' in item) {
			applyBadges(item.children, counts);
		} else if ('to' in item && item.label) {
			const count = counts[item.label];
			item.badge_text = count > 0 ? String(count) : undefined;
		}
	}
}

/** Reads the badges again, while a sidebar is installed */
let badgeRefresh: (() => Promise<void>) | null = null;

/**
 * Reads the badge counts again now, rather than on the next route change: for a page that
 * changes a count while it stays open, such as marking news as read.
 */
export function refreshSidebarBadges(): Promise<void> {
	return badgeRefresh?.() ?? Promise.resolve();
}

/**
 * The sidebar menu of an app: it states what is in the menu, this keeps it in sync with the
 * signed-in user, the active context and the badge counts. What the user may not see is left
 * out while rendering, see `permissions` on the item types.
 */
export function createSidebar(options: SidebarOptions): Plugin {
	return {
		install: (app: App) => {
			// Todo: useAuth() would need a setup context, which a plugin install is not
			const auth = app.config.globalProperties.$auth;
			const router: Router | undefined = app.config.globalProperties.$router;

			const sidebarMenu = useAppSidebarMenuStore();
			const appContext = useAppContextStore();

			// One request at a time: the route hook and the context watcher both ask for badges. A
			// call while one runs asks once more afterwards, as the running one may predate a change.
			let fetching = false;
			let again = false;
			async function fetchBadges(): Promise<void> {
				const path = appContext.getContextPath;
				if (!options.badges || !path) return;
				if (fetching) {
					again = true;
					return;
				}

				fetching = true;
				try {
					const { data } = await axios.get<Record<string, number>>(`${path}/${options.badges}`);
					applyBadges(sidebarMenu.items, data);
				} finally {
					fetching = false;
				}

				if (again) {
					again = false;
					await fetchBadges();
				}
			}

			watch(
				[() => auth.user(), () => appContext.getCurrentContext],
				([user]) => {
					if (!user) return;

					sidebarMenu.set(options.menu(user as AuthUser, appContext));
					fetchBadges();
				},
				{ immediate: true },
			);

			badgeRefresh = fetchBadges;

			router?.afterEach(() => {
				if (auth.user()) fetchBadges();
			});
		},
	};
}
